import {
  ReadonlyStack,
  TwoColumnLayout,
} from '@issuemanager/apps/client/components';
import { IssueTypeEnum } from '@issuemanager/constants';
import { Stack, Typography } from '@mui/material';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  issueType?: string;
  title?: ReactNode;
  description?: ReactNode;
  severity: ReactNode;
  dueDate: ReactNode;
  govmapGroups: ReactNode;
  impact: ReactNode;
  ownerId: ReactNode;
  approverId?: ReactNode;
  watcherIds?: ReactNode;
  // Audit Finding fields
  datePublished?: ReactNode;
  classification?: ReactNode;
  resolution?: ReactNode;
  rootCause?: ReactNode;
  recommendations?: ReactNode;
  managementResponse?: ReactNode;
  // Control Deficiency fields
  remediation?: ReactNode;
};

type RemediationSectionProps = {
  issueType?: string;
  resolution?: ReactNode;
  managementResponse?: ReactNode;
  remediation?: ReactNode;
};

type BaseLayoutProps = {
  title?: ReactNode;
  severity: ReactNode;
  dueDate: ReactNode;
  govmapGroups: ReactNode;
  ownerId: ReactNode;
  approverId?: ReactNode;
  watcherIds?: ReactNode;
};

type DefaultLayoutProps = BaseLayoutProps;

type AuditFindingLayoutProps = BaseLayoutProps & {
  classification?: ReactNode;
  datePublished?: ReactNode;
};

type ControlDeficiencyLayoutProps = BaseLayoutProps & {
  classification?: ReactNode;
};

function DefaultLayout({
  title,
  severity,
  dueDate,
  govmapGroups,
  ownerId,
  approverId,
  watcherIds,
}: DefaultLayoutProps): React.JSX.Element {
  return (
    <TwoColumnLayout
      leftColumn={[title, severity, dueDate, govmapGroups]}
      rightColumn={[ownerId, approverId, watcherIds]}
    />
  );
}

function AuditFindingLayout({
  title,
  severity,
  approverId,
  govmapGroups,
  dueDate,
  classification,
  datePublished,
  ownerId,
  watcherIds,
}: AuditFindingLayoutProps): React.JSX.Element {
  return (
    <TwoColumnLayout
      leftColumn={[title, severity, classification, dueDate, datePublished]}
      rightColumn={[govmapGroups, approverId, ownerId, watcherIds]}
    />
  );
}

function ControlDeficiencyLayout({
  title,
  severity,
  approverId,
  govmapGroups,
  dueDate,
  classification,
  ownerId,
  watcherIds,
}: ControlDeficiencyLayoutProps): React.JSX.Element {
  return (
    <TwoColumnLayout
      leftColumn={[title, severity, classification, dueDate]}
      rightColumn={[govmapGroups, approverId, ownerId, watcherIds]}
    />
  );
}

export function RemediationSection({
  issueType,
  resolution,
  managementResponse,
  remediation,
}: RemediationSectionProps): React.JSX.Element | null {
  const { t } = useTranslation('issue');
  const isAuditFinding = issueType === IssueTypeEnum.AUDIT_FINDING;
  const isControlDeficiency = issueType === IssueTypeEnum.CONTROL_DEFICIENCY;

  if (!isAuditFinding && !isControlDeficiency) {
    return null;
  }

  return (
    <Stack spacing={3}>
      <Typography variant='h3' fontWeight='medium'>
        {t('form.sections.remediation')}
      </Typography>

      <Stack spacing={3}>
        {isControlDeficiency && remediation}
        {managementResponse}
        {resolution}
      </Stack>
    </Stack>
  );
}

export function IssueFields({
  title,
  description,
  severity,
  dueDate,
  govmapGroups,
  impact,
  ownerId,
  approverId,
  watcherIds,
  issueType,
  datePublished,
  classification,
  rootCause,
  recommendations,
}: Props): React.JSX.Element {
  const { t } = useTranslation('issue');
  const isAuditFinding = issueType === IssueTypeEnum.AUDIT_FINDING;
  const isControlDeficiency = issueType === IssueTypeEnum.CONTROL_DEFICIENCY;

  const layoutProps = {
    title,
    severity,
    dueDate,
    govmapGroups,
    ownerId,
    approverId,
    watcherIds,
    classification,
    datePublished,
  };

  const renderLayout = (): React.JSX.Element => {
    if (isAuditFinding) {
      return <AuditFindingLayout {...layoutProps} />;
    }
    if (isControlDeficiency) {
      return <ControlDeficiencyLayout {...layoutProps} />;
    }
    return <DefaultLayout {...layoutProps} />;
  };

  return (
    <Stack data-testid='details' spacing={3}>
      <ReadonlyStack>{renderLayout()}</ReadonlyStack>

      <Typography variant='h3' fontWeight='medium'>
        {t('form.sections.analysis')}
      </Typography>

      <Stack spacing={3}>
        {description}
        {impact}
        {(isAuditFinding || isControlDeficiency) && rootCause}
        {isAuditFinding && recommendations}
      </Stack>
    </Stack>
  );
}
